/**
 * Foreign-key normalization for the whole-report draft save.
 *
 * Every web save re-sends the full report body, so the rows nobody touched go out
 * exactly as the GET returned them: the relation (`bid_item`, `material`,
 * `job_code`, ...) as an object (or, depending on the include, a bare uuid
 * string), and no `*_id` key. The API line-item models only read `*_id` and
 * write NULL for anything absent, so every save used to unlink those relations.
 *
 * The helpers below derive the `*_id` key from whatever shape the row carries.
 */

type Row = Record<string, unknown>;

const isRow = (value: unknown): value is Row => !!value && typeof value === 'object';

/**
 * The id of `row[relation]`, read from (in order):
 *  1. an explicit `row[idKey]` -- edit panels set it to the user's choice, which
 *     must win over the stale relation object still spread in from the GET.
 *     An explicit null / '' means "cleared" and stays null.
 *  2. `row[relation].id` when the relation is an object
 *  3. `row[relation]` when the relation is a bare id string
 *  4. null
 */
export function relationId(row: unknown, relation: string, idKey = `${relation}_id`): string | null {
  if (!isRow(row)) {
    return null;
  }

  const own = row[idKey];
  if (own !== undefined) {
    return (own as string) || null;
  }

  const rel = row[relation];
  if (isRow(rel)) {
    return (rel.id as string) || null;
  }
  if (typeof rel === 'string' && rel) {
    return rel;
  }

  return null;
}

/** A copy of `row` with `<relation>_id` set for each relation. Other keys are kept as-is. */
export function withRelationIds<T>(row: T, relations: string[]): T {
  if (!isRow(row)) {
    return row;
  }

  const ids = relations.reduce((acc, relation) => {
    acc[`${relation}_id`] = relationId(row, relation);
    return acc;
  }, {} as Record<string, string | null>);

  return { ...row, ...ids } as T;
}

/**
 * Attachment / annotation lists hold objects in the GET but the API wants ids.
 * Maps objects to their id and leaves ids alone; a non-array is returned unchanged
 * (an absent list must stay absent).
 */
export function toIdList<T>(list: T): T | unknown[] {
  if (!Array.isArray(list)) {
    return list;
  }

  return list
    .map(o => (isRow(o) ? o.id : o))
    .filter(id => id !== null && id !== undefined && id !== '');
}

/**
 * FK relations each re-sent line-item array carries, keyed by the report body key.
 * Mirrors what the API's Report*Model.save reads (api/src/app/models/report-*-model.js).
 * bid_items (whitelisted field by field) and activity_codes lines (rebuilt as
 * `{ activity_code_id }`) are handled by DailyReportComponent's refactor helpers,
 * which use relationId() too.
 */
export const REPORT_LINE_ITEM_RELATIONS: Record<string, string[]> = {
  material_deliveries: ['bid_item', 'material'],
  labor: ['sub_contractor', 'job_code', 'labor', 'bid_item', 'equipment'],
  equipment: ['sub_contractor', 'equipment', 'bid_item'],
  internal_tests: ['internal_test', 'bid_item', 'station', 'heading'],
  sub_contractors: ['sub_contractor'],
  control_forms: ['control_form'],
};

/**
 * Material-delivery ticket attachments. The API (ReportMaterialDeliveryModel.save)
 * unlinks every ticket of a row on each save and re-links only the attachment ids in
 * `tickets`, so a row re-sent without them loses its tickets.
 */
export function withTicketIds<T>(row: T): T {
  return isRow(row) && Array.isArray(row.tickets)
    ? { ...row, tickets: toIdList(row.tickets) } as T
    : row;
}

/**
 * Edit panels rebuild a row from their form and send no `key` (e.g. `tickets`, which
 * the web has no UI for). Copy the list from the stored row with the same id so the
 * save doesn't drop it. A list the panel does send wins; new rows (no id) are untouched.
 */
export function carryOverUnsentList<T>(existing: unknown[] | null | undefined, incoming: T[], key: string): T[] {
  const byId = new Map<string, Row>();
  (existing || []).forEach(row => {
    if (isRow(row) && row.id !== null && row.id !== undefined) {
      byId.set(String(row.id), row);
    }
  });

  return (incoming || []).map(row => {
    if (!isRow(row) || row[key] !== undefined || row.id === null || row.id === undefined) {
      return row;
    }
    const previous = byId.get(String(row.id));
    return previous && previous[key] !== undefined ? { ...row, [key]: previous[key] } as T : row;
  });
}

/** Arrays whose `annotations` the API resolves as ids (objects there fail validation). */
const ANNOTATED_LINE_ITEMS = ['material_deliveries', 'activities', 'internal_tests', 'labor', 'equipment'];

/**
 * Sets every FK `*_id` on every re-sent line item of a report body, whatever widget
 * was edited. Mutates nothing: returns a new report object.
 */
export function normalizeReportLineItemIds<T>(report: T): T {
  if (!isRow(report)) {
    return report;
  }

  const out: Row = { ...report };

  Object.entries(REPORT_LINE_ITEM_RELATIONS).forEach(([key, relations]) => {
    const rows = out[key];
    if (Array.isArray(rows)) {
      out[key] = rows.map(row => withRelationIds(row, relations));
    }
  });

  if (Array.isArray(out.material_deliveries)) {
    out.material_deliveries = out.material_deliveries.map(withTicketIds);
  }

  ANNOTATED_LINE_ITEMS.forEach(key => {
    const rows = out[key];
    if (Array.isArray(rows)) {
      out[key] = rows.map(row => (isRow(row) && Array.isArray(row.annotations)
        ? { ...row, annotations: toIdList(row.annotations) }
        : row));
    }
  });

  return out as T;
}
